/**
 * useHandoutSeen - who has opened which handout (v9.8.0).
 *
 * Everyone listens to the session's `handoutSeen` documents (one per person,
 * engine/handouts/seen); each person writes only their own, which the rules
 * enforce (`member-own`). A player gets `unread` (what was shown to them
 * and they have not opened) and `mark`; the GM gets `seenOf` ("seen by 2 of
 * 3") and marks nothing: the GM is not somebody a handout is shown to.
 *
 * A plain write, not a transaction: two tabs of one account marking in the
 * same instant could lose one mark, and all that costs is a handout that
 * reads as new once more.
 *
 * @returns {{ unread: Set<string>, mark: (id: string) => void, seenOf: (h) => { seen, of } }}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  markSeen,
  pruneSeen,
  readSeenMarks,
  seenCount,
  seenDoc,
  unreadHandouts,
  type Handout,
  type SeenMarks,
} from '../engine/handouts';
import type { CollectionRead } from '../backend/types';
import { warnIfRejected } from '../diagnostics/log';

interface SeenMp {
  onHandoutSeenUpdate: (sessionId: string, cb: (read: CollectionRead<Record<string, unknown>>) => void) => () => void;
  setHandoutSeen: (sessionId: string, uid: string, doc: Record<string, unknown>) => Promise<void>;
}

interface Params {
  sessionId: string | null;
  uid: string;
  isGm: boolean;
  handouts: Handout[];
  people: readonly { uid: string; isGm: boolean }[];
  mp: SeenMp;
  now?: () => number;
}

type ByUid = Record<string, SeenMarks>;
const NOBODY: ByUid = {};
const NOTHING: SeenMarks = {};

/** The stored documents as marks by owner; one that names no owner is dropped. */
function byOwner(entries: CollectionRead<Record<string, unknown>>['entries']): ByUid {
  const owned = entries.filter(({ data }) => typeof data.uid === 'string' && data.uid);
  return Object.fromEntries(owned.map(({ data }) => [data.uid as string, readSeenMarks(data.data)]));
}

/** Everyone's marks, by the owner each document names. */
function useSeenFeed(sessionId: string | null, mp: SeenMp): ByUid {
  const [byUid, setByUid] = useState<ByUid>(NOBODY);
  useEffect(() => {
    if (!sessionId) return undefined;
    const stop = mp.onHandoutSeenUpdate(sessionId, (read) => setByUid(byOwner(read.entries)));
    return () => {
      stop();
      setByUid(NOBODY);
    };
  }, [sessionId, mp]);
  return byUid;
}

type MarkDeps = Pick<Params, 'sessionId' | 'uid' | 'isGm' | 'handouts' | 'mp'> & {
  stored: SeenMarks;
  now: () => number;
};

/** My marks (what the server holds plus what this screen wrote and has not heard back), and the write. */
function useMyMarks({ sessionId, uid, isGm, handouts, mp, stored, now }: MarkDeps) {
  // Ahead of the listener: without it a handout opened twice in a second would be written
  // twice, and its pip would flicker back.
  const [ahead, setAhead] = useState<SeenMarks>(NOTHING);
  const mine = useMemo(() => ({ ...stored, ...ahead }), [stored, ahead]);
  const latest = useRef({ mine, handouts });
  latest.current = { mine, handouts };
  const mark = useCallback(
    (id: string) => {
      const { mine: cur, handouts: feed } = latest.current;
      const h = feed.find((x) => x.id === id);
      if (!sessionId || !uid || isGm || !h) return;
      const at = now();
      const next = markSeen(cur, h, at);
      if (next === cur) return;
      setAhead((a) => ({ ...a, [id]: at }));
      warnIfRejected(mp.setHandoutSeen(sessionId, uid, seenDoc(uid, pruneSeen(next, feed), at)), 'handouts', 'seen');
    },
    [sessionId, uid, isGm, mp, now],
  );
  return { mine, mark };
}

export default function useHandoutSeen({ sessionId, uid, isGm, handouts, people, mp, now = Date.now }: Params) {
  const byUid = useSeenFeed(sessionId, mp);
  const stored = byUid[uid] ?? NOTHING;
  const { mine, mark } = useMyMarks({ sessionId, uid, isGm, handouts, mp, stored, now });
  const unread = useMemo(
    () => new Set(isGm ? [] : unreadHandouts(handouts, mine).map((h) => h.id)),
    [isGm, handouts, mine],
  );
  const seenOf = useCallback((h: Handout) => seenCount(h, byUid, people), [byUid, people]);
  return useMemo(() => ({ unread, mark, seenOf }), [unread, mark, seenOf]);
}
